import { useTranslation } from "react-i18next";
import { CalendarCheck } from "lucide-react";
import { WEEKDAY_KEYS } from "@/pages/daycare-hotel/plans/FixedWeekdaysField";
import { formatDateBR } from "@/lib/date-utils";

export interface PlanDaysCardProps {
  name: string;
  weekdays: number[];
  /** Próximas datas do plano; mostra as seis primeiras. */
  dates: string[];
  /** Chave i18n de um selo (ex.: "planDays.draftBadge"). */
  badge?: string;
  onUndo?: () => void;
  /** 2 = quinzenal: o rótulo dos dias avisa que é semana sim, semana não. */
  weekInterval?: number;
}

export function PlanDaysCard({ name, weekdays, dates, badge, onUndo, weekInterval = 1 }: PlanDaysCardProps) {
  const { t } = useTranslation("daycareHotel");
  return (
    <div className="grid gap-2 rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarCheck aria-hidden="true" size={16} className="text-primary" />
        <span className="text-sm font-semibold text-foreground">{name}</span>
        {badge ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">{t(badge)}</span> : null}
        {onUndo ? (
          <button
            type="button"
            onClick={onUndo}
            className="ms-auto rounded-lg border border-border px-3 py-1 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t("planDays.undo")}
          </button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">{t("planDays.weekdaysLabel")}</span>
        {weekdays.map((weekday) => (
          <span key={weekday} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
            {t(WEEKDAY_KEYS[weekday])}
          </span>
        ))}
        {weekInterval === 2 ? <span className="text-xs text-muted-foreground">{t("planDays.biweekly")}</span> : null}
      </div>
      {dates.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{t("planDays.nextDates")}</span>
          {dates.slice(0, 6).map((date) => (
            <span key={date} className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {formatDateBR(date)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
